import { ComponentPropsWithoutRef, createContext, useContext, useMemo } from 'react';
import { Button } from '@givebutter/primitives/button';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CreateCampaignDialogForm } from 'components/create-campaign-dialog/CreateCampaignDialogContext';
import { UseFormLabel, useGivebutterFormContext } from 'components/form';
import * as Form from 'components/form';
import { twMerge } from 'tailwind-merge';

interface IFeatureCheckboxItemContext {
    checked: boolean;
}
const FeatureCheckboxItemContext = createContext<IFeatureCheckboxItemContext>({
    checked: false,
});

interface IFeatureCheckboxItemRoot extends ComponentPropsWithoutRef<'div'> {
    value: keyof Omit<CreateCampaignDialogForm, 'campaignName' | 'type'>;
    posthogEventDispatch: () => void;
}
const FeatureCheckboxItemRoot = ({
    value,
    posthogEventDispatch,
    children,
}: IFeatureCheckboxItemRoot) => {
    const { control, path, watch, setValue } = useGivebutterFormContext();

    const [checked] = watch([value]);

    const ctx = useMemo(() => ({ checked }), [checked]);

    return (
        <FeatureCheckboxItemContext.Provider value={ctx}>
            <div>
                <Form.UseFormCheckboxInput
                    control={control}
                    name={value}
                    tabIndex={-1}
                    className="peer sr-only"
                />
                <UseFormLabel
                    name={path(value)}
                    className={twMerge(
                        'flex w-full cursor-pointer justify-between gap-3 rounded-xl p-4 ring-1 ring-gray-200',
                        'peer-radix-state-checked:bg-blue-25 peer-radix-state-checked:ring-brand-cobalt-primary peer-radix-state-checked:ring-2 peer-checked:bg-purple-400 peer-aria-checked:bg-red-500',
                    )}
                >
                    <div className="flex gap-3">{children}</div>
                    {checked ? (
                        <Button
                            variant="transparent"
                            className="hover:bg-blue-100"
                            onClick={() => setValue(value, false)}
                        >
                            Remove
                        </Button>
                    ) : (
                        <Button
                            variant="outline"
                            color="neutral"
                            onClick={() => {
                                setValue(value, true);
                                posthogEventDispatch();
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faPlus}
                                fontSize="medium"
                                className="pointer-events-none"
                            />{' '}
                            Add
                        </Button>
                    )}
                </UseFormLabel>
            </div>
        </FeatureCheckboxItemContext.Provider>
    );
};

interface IFeatureCheckboxItemTitle extends ComponentPropsWithoutRef<'p'> {}
const FeatureCheckboxItemTitle = ({ className = '', ...rest }: IFeatureCheckboxItemTitle) => {
    return <p className={twMerge('font-medium', className)} {...rest} />;
};

interface IFeatureCheckboxItemDescription extends ComponentPropsWithoutRef<'p'> {}
const FeatureCheckboxItemDescription = ({
    className = '',
    ...rest
}: IFeatureCheckboxItemDescription) => {
    return <p className={twMerge('font-normal text-gray-500', className)} {...rest} />;
};

interface IFeatureCheckboxItemIcon extends ComponentPropsWithoutRef<'div'> {
    icon: IconDefinition;
}
const FeatureCheckboxItemIcon = ({ className = '', icon, ...rest }: IFeatureCheckboxItemIcon) => {
    const { checked } = useContext(FeatureCheckboxItemContext);
    return (
        <div
            className={twMerge(
                'flex aspect-square size-10 items-center justify-center rounded-full',
                checked
                    ? 'bg-brand-cobalt-light [&>svg]:text-brand-cobalt-primary'
                    : 'bg-gray-200 [&>svg]:text-gray-500',
                className,
            )}
            {...rest}
        >
            <FontAwesomeIcon icon={checked ? faCheck : icon} className="size-5" />
        </div>
    );
};

const Root = FeatureCheckboxItemRoot;
const Title = FeatureCheckboxItemTitle;
const Description = FeatureCheckboxItemDescription;
const Icon = FeatureCheckboxItemIcon;

export {
    FeatureCheckboxItemRoot,
    FeatureCheckboxItemTitle,
    FeatureCheckboxItemDescription,
    FeatureCheckboxItemIcon,
    //
    Root,
    Title,
    Description,
    Icon,
};
